import { useEffect, useState } from 'react';
import type { User } from '@sharp8/types';
import { Home } from './views/Home';
import { Match } from './views/Match';
import { You } from './views/You';
import { Compete } from './views/Compete';
import { Matches } from './views/Matches';
import { Dock, type DockTab } from './components/Dock';
import { BuildBadge } from './components/BuildBadge';
import { tg } from './lib/telegram';
import { fetchMe } from './lib/me';

/**
 * Tiny routing state — flat union, no router lib. Telegram mini-apps
 * are typically shallow; a global state + BackButton handling covers
 * the brief's spine.
 */
/** Which tab a match detail was opened from. Drives BackButton +
 *  in-app onBack so closing the detail returns to the entry point
 *  (Home → Home, Matches → Matches, You → You) instead of always
 *  going Home. */
type MatchParent = 'home' | 'matches' | 'you';

/** Optional sheet to auto-open when landing on a match detail.
 *  Lets Home's "Place bet" + "Debate" CTAs deep-link past the bet-rec
 *  card straight into the relevant action. */
export type MatchIntent = 'bet' | 'debate';

export type Route =
  | { name: 'home' }
  | { name: 'matches' }
  | { name: 'compete' }
  | { name: 'you' }
  | { name: 'match'; matchId: string; from: MatchParent; openSheet?: MatchIntent; edgeId?: string };

export function App() {
  const [route, setRoute] = useState<Route>({ name: 'home' });
  const [me, setMe] = useState<{ user: User; guest: boolean } | null>(null);

  // Single bankroll-setter shared by every consumer that may receive
  // a new value (PlaceBetSheet on placement; picks SSE on settle).
  const setBankroll = (newBankroll: number) => {
    setMe((prev) => prev && !prev.guest
      ? { ...prev, user: { ...prev.user, bankroll: newBankroll } }
      : prev);
  };

  // Load the authenticated user on first mount.
  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((m) => { if (!cancelled) setMe(m); })
      .catch((e) => console.warn('[sharp8] /me failed:', e));
    return () => { cancelled = true; };
  }, []);

  // Wire Telegram BackButton to navigate back when we're in a detail view.
  // Back target is the tab the user opened the match from — Home → Home,
  // Matches → Matches — not a hardcoded Home.
  useEffect(() => {
    const w = tg();
    if (!w) return;

    if (route.name !== 'match') {
      try { w.BackButton.hide(); } catch { /* ignore */ }
      return;
    }

    const parent = route.from;
    const onBack = () => setRoute({ name: parent });
    try {
      w.BackButton.onClick(onBack);
      w.BackButton.show();
    } catch { /* SDK can throw on cold-start, ignore */ }
    return () => {
      try {
        w.BackButton.offClick(onBack);
        w.BackButton.hide();
      } catch { /* ignore */ }
    };
  }, [route.name, route.name === 'match' ? route.from : null]);

  // Map "match" route back to its parent tab when computing active dock
  // state — so the dock highlight stays on Matches when you're inside
  // a match opened from Matches, instead of always switching to Home.
  const activeTab: DockTab =
    route.name === 'match' ? route.from : (route.name as DockTab);

  return (
    <div className="app">
      <header className="header">
        <a href="/" className="header__brand" onClick={(e) => { e.preventDefault(); setRoute({ name: 'home' }); }}>
          <img src="/brand/logo-128.png" alt="Sharp8" />
          <span className="header__brand-word">
            Sharp<span className="dot">8</span>
          </span>
        </a>
        <div className="header__right">
          {me && !me.guest && (
            <button className="header__user" aria-label={me.user.username ?? 'You'}>
              {me.user.photoUrl ? (
                <img src={me.user.photoUrl} alt="" className="header__avatar" />
              ) : (
                <span className="header__avatar header__avatar--fallback">
                  {(me.user.username ?? 'U')[0]?.toUpperCase()}
                </span>
              )}
              <span className="header__username">{me.user.username}</span>
            </button>
          )}
          <button className="header__action" aria-label="Notifications">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 01-3.46 0" />
            </svg>
          </button>
        </div>
      </header>

      <main className="app__main">
        {route.name === 'home'    && <Home    me={me} onOpenMatch={(id, intent, edgeId) => setRoute({ name: 'match', matchId: id, from: 'home',    openSheet: intent, edgeId })} />}
        {route.name === 'matches' && <Matches onOpenMatch={(id) =>             setRoute({ name: 'match', matchId: id, from: 'matches' })} />}
        {route.name === 'compete' && <Compete me={me} />}
        {route.name === 'you'     && (
          <You
            me={me}
            onBankrollChange={setBankroll}
            onOpenMatch={(id) => setRoute({ name: 'match', matchId: id, from: 'you' })}
          />
        )}
        {route.name === 'match'   && (
          <Match
            matchId={route.matchId}
            edgeId={route.edgeId}
            backTo={route.from}
            openSheet={route.openSheet}
            onBack={() => setRoute({ name: route.from })}
            bankroll={me && !me.guest ? me.user.bankroll : undefined}
            onBankrollChange={setBankroll}
          />
        )}
      </main>

      <Dock active={activeTab} onChange={(tab) => setRoute({ name: tab })} />
      <BuildBadge />
    </div>
  );
}

function PlaceholderView({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div style={{ padding: '80px 32px', textAlign: 'center', color: 'var(--ink-soft)' }}>
      <div style={{
        fontSize: 22,
        fontWeight: 700,
        letterSpacing: '-0.03em',
        color: 'var(--ink)',
        marginBottom: 8,
      }}>
        {title}
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.5 }}>{subtitle}</div>
    </div>
  );
}
